import { Component, Output, EventEmitter, inject } from '@angular/core';
import { InputFieldComponent } from '../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../ui/customizable-button/customizable-button.component';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { IContact, IOrganization, ITag } from '../../../types/contactModule';
import { countries } from '../../../../utils/countriesList';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { ReactiveFormsModule, Validators, FormBuilder } from '@angular/forms';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { SecurePersistStateService } from '../../../services/storage/local-storage.service';
import { GroupSelectionInputComponent } from '../../inputs/group-selection-input/group-selection-input.component';
import { AssignOrganizationsTableComponent } from '../../tables/assign-organizations-modal/assign-organizations-modal.component';
import { AssignTagsModalComponent } from '../../tables/assign-tags-modal/assign-tags-modal.component';
import { parseFlexiblePhoneNumber } from '../../../../utils/utils';

export type IContactFormDataKeys =
  | 'firstName'
  | 'lastName'
  | 'emailAddress'
  | 'primaryContactNumber'
  | 'gender'
  | 'birthDate'
  | 'country'
  | 'city'
  | 'tag'
  | 'organization';

@Component({
  selector: 'create-contact-form',
  standalone: true,
  imports: [
    InputFieldComponent,
    CustomizableButtonComponent,
    ModalFormContainerComponent,
    ReactiveFormsModule,
    GroupSelectionInputComponent,
    AssignOrganizationsTableComponent,
    AssignTagsModalComponent,
    NgxMatInputTelComponent,
  ],
  templateUrl: './create-contact-form.component.html',
  styleUrls: ['./contact-form.css', './create-contact-form.component.css'],
})
export class CreateContactFormComponent {
  constructor(private builder: FormBuilder) {}
  contactsService = inject(ContactsService);
  storageService = inject(SecurePersistStateService);
  countries = countries;

  selectedOrganizations: IOrganization[] = [];
  selectedTags: ITag[] = [];

  // modal controls
  showAddOrganizationModal: boolean = false;
  showAddTagModal: boolean = false;

  openAddOrganizationModal() {
    this.showAddOrganizationModal = true;
  }
  closeAddOrganizationModal() {
    this.showAddOrganizationModal = false;
  }
  openAddTagModal() {
    this.showAddTagModal = true;
  }
  closeAddTagModal() {
    this.showAddTagModal = false;
  }

  @Output() closeModalEvent = new EventEmitter();

  contactForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: [''],
    emailAddress: ['', [Validators.email]],
    primaryContactNumber: ['', Validators.required],
    gender: [''],
    birthDate: [''],
    country: [''],
    // Dial code from the phone field's country picker. Not required: when the
    // picker never reports a country, it is worked out from the number itself.
    zipCode: [''],
    city: [''],
  });

  submitted: boolean = false;
  /** Why the last save failed, in words the person can act on. */
  saveError: string = '';

  closeModal() {
    this.closeModalEvent.emit();
  }

  getFormInput(name: IContactFormDataKeys) {
    return this.contactForm.get(name);
  }

  countryChanged(country: any) {
    this.contactForm.patchValue({
      zipCode: `+${country.dialCode}`,
    });
  }

  get emailErrorMsg() {
    const email = this.getFormInput('emailAddress');
    if (email?.errors?.['email']) {
      return 'Enter an email address like name@example.com';
    }
    return '';
  }

  get phoneErrorMsg() {
    const phone = this.contactForm.controls.primaryContactNumber;
    if (!phone.invalid) return '';
    return phone.errors?.['required'] ? 'Enter a phone number' : 'Enter a valid phone number';
  }

  // update the organization selection in the parent cmpnt
  updateSelectedOrganizations(selections: IOrganization[]) {
    this.selectedOrganizations = [...(selections ?? [])];
  }

  // update the organization selection in the parent cmpnt
  updateSelectedTags(selections: ITag[]) {
    this.selectedTags = [...(selections ?? [])];
  }

  removeSelectedOrganization(organization: any) {
    this.selectedOrganizations = this.selectedOrganizations.filter(
      (org) => org.id !== organization.id
    );
  }

  removeSelectedTag(tag: any) {
    const passedTag = tag as ITag;
    this.selectedTags = this.selectedTags.filter((el) => el.id !== passedTag.id);
  }

  get contactServiceLoading() {
    return this.contactsService.isLoading;
  }

  get contactServiceFetching() {
    return this.contactsService.isFetching;
  }

  handleSubmit() {
    this.submitted = true;
    this.saveError = '';
    if (!this.contactForm.value.zipCode) {
      const parsed = parseFlexiblePhoneNumber(this.contactForm.value.primaryContactNumber);
      if (parsed) this.contactForm.patchValue({ zipCode: `+${parsed.countryCallingCode}` });
    }
    if (this.contactForm.valid) {
      this.contactsService
        .createContact({
          ...(this.contactForm.value as IContact),
          companyEntities: this.selectedOrganizations,
          entityTags: this.selectedTags,
        })
        .subscribe({
          next: () => {
            this.closeModal();
          },
          error: (err) => {
            const message = `${err?.error?.message ?? err?.error?.title ?? err?.message ?? ''}`;
            this.saveError = /duplicate/i.test(message)
              ? 'A contact with this phone number already exists.'
              : 'The contact was not saved. Check the details and try again.';
          },
          complete: () => {},
        });
    }
  }
}
